/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import PostalCodeFields from '../forms/postal-code-fields'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {sendViewErrorEvent} from '../../utils/gtm-utils'

export const usePostalCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()

    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )
    const submitCode = async ({postalCode}, onClose) => {
        try {
            await updateShippingAddressForShipment.mutateAsync({
                parameters: {
                    basketId: basket?.basketId,
                    shipmentId: 'me',
                    useAsBilling: false
                },
                body: {
                    address1: 'basketOnly',
                    countryCode: 'GB',
                    postalCode
                }
            })

            form.reset({postalCode: ''})

            onClose()
        } catch (e) {
            // GTM Modification
            sendViewErrorEvent('Enter a valid postcode')
            form.setError(
                'postalCode',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage:
                            'Enter a valid postcode',
                        id: 'use_postal_code_fields.error.enter_postal_code'
                    })
                },
                {shouldFocus: true}
            )
        }
    }

    const updatePostalCode = async (postalCode) => {
        form.setValue('postalCode', postalCode)
        await form.handleSubmit(submitCode)()
    }

    return {form, submitCode, updatePostalCode}
}

export const PostalCode = ({form, submitCode, onClose}) => {
    return (
        <Box
            data-testid="postal-code-form"
            as="form"
            background="white"
            onSubmit={form.handleSubmit((data) => submitCode(data, onClose))}
        >
            <PostalCodeFields form={form} maxWidth="350px" />
        </Box>
    )
}

PostalCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitCode: PropTypes.func.isRequired,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object,
    /** Function called when the accordion is closed. */
    onClose: PropTypes.func
}

export default PostalCode
